import type {
  FaultSeverity,
  MachineStatus,
} from '../../services/ShiftAnalysisService';

/**
 * Severity colors for fault bars. Tuned for contrast on both light and dark
 * backgrounds (this is the dark-mode contrast issue we hit on Live Shift).
 */
export const SEVERITY_COLORS: Record<
  FaultSeverity,
  { bar: string; barTo: string; text: string; label: string }
> = {
  critical: { bar: '#ef4444', barTo: '#b91c1c', text: '#fff', label: 'Critical' },
  major: { bar: '#f97316', barTo: '#c2410c', text: '#fff', label: 'Major' },
  minor: { bar: '#eab308', barTo: '#a16207', text: '#1f2937', label: 'Minor' },
};

/** Machine status colors (good/watch/bad/no_data). */
export const STATUS_COLORS: Record<
  MachineStatus,
  { color: string; bg: string; ring: string; label: string; emoji: string }
> = {
  good: {
    color: '#10b981',
    bg: 'rgba(16,185,129,0.12)',
    ring: 'rgba(16,185,129,0.4)',
    label: 'Good',
    emoji: '🟢',
  },
  watch: {
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.12)',
    ring: 'rgba(245,158,11,0.4)',
    label: 'Watch',
    emoji: '🟡',
  },
  bad: {
    color: '#ef4444',
    bg: 'rgba(239,68,68,0.12)',
    ring: 'rgba(239,68,68,0.4)',
    label: 'Bad',
    emoji: '🔴',
  },
  no_data: {
    color: '#9ca3af',
    bg: 'rgba(156,163,175,0.12)',
    ring: 'rgba(156,163,175,0.35)',
    label: 'No data',
    emoji: '⚪',
  },
};

/**
 * Human-friendly duration from seconds.
 *   95   → "1m 35s"
 *   3725 → "1h 2m"
 *   45   → "45s"
 */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

/** Precise clock duration "HH:MM:SS" from seconds. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function msToSeconds(ms: number): number {
  return ms / 1000;
}

/** 0–1 fraction → "85%". */
export function formatPercent(fraction: number, digits = 0): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** Compact thousands: 106907 → "106.9k", 549 → "549". */
export function formatCompact(value: number): string {
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return String(Math.round(value));
}

/**
 * Parse a backend timestamp into a comparable epoch, treating it as WALL-CLOCK
 * (the shift's local time) regardless of the browser timezone. Backend sends
 * e.g. "2026-02-10 06:00:01" meaning 06:00 in the customer's shift time; we must
 * display 06:00, never shift it by the viewer's timezone. We therefore parse the
 * Y-M-D H:M:S components and anchor them to UTC, and format with UTC getters so
 * the wall-clock numbers are preserved. Any trailing zone/offset is ignored.
 * Returns NaN on failure.
 */
export function parseTs(ts: string): number {
  if (!ts) return NaN;
  const m = ts.match(
    /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!m) {
    const fallback = new Date(ts).getTime();
    return Number.isNaN(fallback) ? NaN : fallback;
  }
  const [, y, mo, d, h, mi, s] = m;
  return Date.UTC(+y, +mo - 1, +d, +h, +mi, s ? +s : 0);
}

/** "2026-06-05 06:00:01" → "06:00" (wall-clock, no timezone shift). */
export function formatTimeOfDay(ts: string): string {
  const t = parseTs(ts);
  if (Number.isNaN(t)) return '';
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

import type {
  LineSummary,
  StopCause,
  CulpritMachine,
} from '../../services/ShiftAnalysisService';

export interface Verdict {
  key: 'strong' | 'mixed' | 'tough' | 'nodata';
  label: string;
  emoji: string;
  color: string;
  /** CSS gradient for the hero background. */
  gradient: string;
}

/** Map the line's average efficiency to a plain-language verdict. */
export function getVerdict(avgEfficiency: number | undefined): Verdict {
  const e = avgEfficiency ?? 0;
  if (!e) {
    return {
      key: 'nodata',
      label: 'No data',
      emoji: '⚪',
      color: '#9ca3af',
      gradient: 'linear-gradient(135deg, #64748b, #475569)',
    };
  }
  if (e >= 0.85) {
    return {
      key: 'strong',
      label: 'Strong shift',
      emoji: '🟢',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #059669, #10b981)',
    };
  }
  if (e >= 0.6) {
    return {
      key: 'mixed',
      label: 'Mixed shift',
      emoji: '🟡',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #d97706, #f59e0b)',
    };
  }
  return {
    key: 'tough',
    label: 'Tough shift',
    emoji: '🔴',
    color: '#ef4444',
    gradient: 'linear-gradient(135deg, #dc2626, #ef4444)',
  };
}

/**
 * Build a one-sentence, non-expert plain-language summary of the shift:
 * which machine caused most of the downtime, with what fault, how often, and
 * the line efficiency.
 */
export function buildSummary(
  line: LineSummary | null,
  culprit: CulpritMachine | undefined,
  topCause: StopCause | undefined,
): string {
  const effTxt = line ? `${Math.round((line.avg_efficiency ?? 0) * 100)}%` : '—';

  if (!culprit && !topCause) {
    return `The line ran at ${effTxt} efficiency with no internal stop causes recorded.`;
  }

  const machine = culprit?.machine_name ?? topCause?.machine_name ?? 'A machine';
  if (topCause) {
    return `${machine} caused most of the downtime — ${topCause.fault_text} stopped it ${topCause.occurrences}× (about ${formatDuration(topCause.total_seconds)}). The line ran at ${effTxt} efficiency.`;
  }
  return `${machine} was the biggest contributor with ${culprit?.internal_stops ?? 0} internal stops (about ${formatDuration(culprit?.internal_seconds ?? 0)}). The line ran at ${effTxt} efficiency.`;
}

export interface ScoreBand {
  key: 'excellent' | 'good' | 'behind' | 'attention';
  label: string;
  emoji: string;
  color: string;
}

export interface ShiftScore {
  score: number; // 0–100
  efficiencyPct: number;
  utilizationPct: number;
  reliabilityPct: number;
  band: ScoreBand;
}

function scoreBand(score: number): ScoreBand {
  if (score >= 75)
    return { key: 'excellent', label: 'Excellent', emoji: '🏆', color: '#10b981' };
  if (score >= 50)
    return { key: 'good', label: 'On track', emoji: '📊', color: '#3b82f6' };
  if (score >= 25)
    return { key: 'behind', label: 'Behind', emoji: '📉', color: '#f59e0b' };
  return { key: 'attention', label: 'Needs attention', emoji: '⚠️', color: '#ef4444' };
}

/**
 * Composite 0–100 Shift Score for a completed shift, derived only from the
 * server-provided line rollup (the frontend does no KPI calculation beyond this
 * transparent weighting):
 *
 *   score = efficiency×0.45 + utilization×0.30 + reliability×0.25
 *
 * `reliability` is the share of machines that ran healthy — `good` counts full,
 * `watch` partial, `bad` little; `no_data` (idle) machines are excluded.
 */
export function computeShiftScore(line: LineSummary | null): ShiftScore {
  const efficiencyPct = (line?.avg_efficiency ?? 0) * 100;
  const utilizationPct = (line?.avg_utilization ?? 0) * 100;

  const good = line?.good_count ?? 0;
  const watch = line?.watch_count ?? 0;
  const bad = line?.bad_count ?? 0;
  const active = good + watch + bad;
  const reliabilityPct =
    active > 0 ? (good * 100 + watch * 60 + bad * 20) / active : 0;

  const score = Math.round(
    Math.min(
      100,
      efficiencyPct * 0.45 + utilizationPct * 0.3 + reliabilityPct * 0.25,
    ),
  );

  return {
    score,
    efficiencyPct,
    utilizationPct,
    reliabilityPct,
    band: scoreBand(score),
  };
}

/** Distinct, color-blind-friendly palette for per-machine series/segments. */
export const MACHINE_PALETTE = [
  '#3b82f6',
  '#ef4444',
  '#10b981',
  '#f59e0b',
  '#a855f7',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
  '#f97316',
  '#14b8a6',
  '#6366f1',
  '#eab308',
];

/** Stable machine_sn → color map (by index), shared across charts. */
export function buildMachineColorMap(
  machines: { machine_sn: string }[],
): Map<string, string> {
  const map = new Map<string, string>();
  machines.forEach((m, i) => {
    map.set(m.machine_sn, MACHINE_PALETTE[i % MACHINE_PALETTE.length]);
  });
  return map;
}

import type { StateCategory } from '../../services/ShiftAnalysisService';

/** Colors + labels for operating-state categories (status timeline). */
export const STATE_COLORS: Record<
  StateCategory,
  { color: string; colorTo: string; label: string }
> = {
  running: { color: '#10b981', colorTo: '#059669', label: 'Running' },
  waiting_ext: { color: '#f59e0b', colorTo: '#d97706', label: 'Waiting (line)' },
  waiting_int: { color: '#ef4444', colorTo: '#dc2626', label: 'Waiting (own)' },
  stopped: { color: '#b91c1c', colorTo: '#7f1d1d', label: 'Stopped' },
  setup: { color: '#6366f1', colorTo: '#4f46e5', label: 'Setup' },
  standby: { color: '#94a3b8', colorTo: '#64748b', label: 'Standby' },
};

/** Display order for state categories in legends/stacks (best → worst). */
export const STATE_ORDER: StateCategory[] = [
  'running',
  'standby',
  'setup',
  'waiting_ext',
  'waiting_int',
  'stopped',
];
